import { useState, useCallback } from 'react';
import { attendanceAPI } from '../../services/api';
import { Search, Download, AlertCircle, CheckCircle } from 'lucide-react';

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const STATUS_CONFIG = {
  valid:          { label: 'Valid',          cls: 'bg-green-100 text-green-800'  },
  partial:        { label: 'Partial',        cls: 'bg-yellow-100 text-yellow-800' },
  left_before_ot: { label: 'Left Early',     cls: 'bg-red-100 text-red-800'      },
  no_clock_out:   { label: 'No Clock Out',   cls: 'bg-orange-100 text-orange-800' },
  no_record:      { label: 'No Record',      cls: 'bg-red-100 text-red-800'      },
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, day] = iso.split('-');
  return new Date(y, m - 1, day).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const fmtHours = (mins) => {
  if (mins == null) return '—';
  return (mins / 60).toFixed(2);
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, cls: 'bg-gray-100 text-gray-700' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
};

const StatBadge = ({ label, value, cls }) => (
  <div className={`flex flex-col items-center px-4 py-3 rounded-lg border ${cls}`}>
    <span className="text-2xl font-bold">{value}</span>
    <span className="text-xs mt-0.5">{label}</span>
  </div>
);

const OTVerification = () => {
  const [startDate, setStartDate] = useState(monthStart());
  const [endDate, setEndDate]     = useState(today());
  const [records, setRecords]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [issuesOnly, setIssuesOnly] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await attendanceAPI.getOTVerification({ start_date: startDate, end_date: endDate });
      setRecords(res.data.records);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const displayed = records
    ? (issuesOnly ? records.filter(r => r.verification !== 'valid') : records)
    : [];

  const counts = records ? {
    total:  records.length,
    valid:  records.filter(r => r.verification === 'valid').length,
    partial: records.filter(r => r.verification === 'partial').length,
    issues: records.filter(r => ['left_before_ot', 'no_clock_out', 'no_record'].includes(r.verification)).length,
  } : null;

  const exportCSV = () => {
    if (!displayed.length) return;
    const headers = ['Staff No','Name','Department','Date','OT Start','OT End','OT Hrs','Clock In','Clock Out','Status'];
    const rows = displayed.map(r => [
      r.staff_no, r.name, r.department,
      fmtDate(r.date),
      fmtTime(r.ot_start), fmtTime(r.ot_end),
      fmtHours(r.ot_mins),
      fmtTime(r.clock_in), fmtTime(r.clock_out),
      STATUS_CONFIG[r.verification]?.label || r.verification,
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ot-verification-${startDate}-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <button onClick={fetch} disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
            <Search className="h-4 w-4 mr-2" />
            {loading ? 'Loading…' : 'Fetch'}
          </button>
          {records && (
            <>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none ml-2">
                <input type="checkbox" checked={issuesOnly} onChange={e => setIssuesOnly(e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded" />
                Issues only
              </label>
              <button onClick={exportCSV}
                className="ml-auto inline-flex items-center px-3 py-2 border border-gray-300 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50">
                <Download className="h-4 w-4 mr-1" /> Export CSV
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-center text-red-700 text-sm">
          <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />{error}
        </div>
      )}

      {/* Summary strip */}
      {counts && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatBadge label="Total OT Records" value={counts.total}   cls="bg-gray-50 border-gray-200 text-gray-700" />
          <StatBadge label="Valid"             value={counts.valid}   cls="bg-green-50 border-green-200 text-green-700" />
          <StatBadge label="Partial"           value={counts.partial} cls="bg-yellow-50 border-yellow-200 text-yellow-700" />
          <StatBadge label="Issues"            value={counts.issues}  cls={counts.issues > 0 ? 'bg-red-50 border-red-200 text-red-700' : 'bg-gray-50 border-gray-200 text-gray-700'} />
        </div>
      )}

      {/* Table */}
      {records && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {displayed.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CheckCircle className="h-10 w-10 mx-auto mb-2 text-green-400" />
              {issuesOnly ? 'No issues found — all OT records are valid.' : 'No approved OT records in this date range.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Staff No','Name','Department','Date','OT Period','OT Hrs','Clock In','Clock Out','Status'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {displayed.map(r => (
                    <tr key={r.ot_id} className={`hover:bg-gray-50 ${r.verification !== 'valid' ? 'bg-red-50/30' : ''}`}>
                      <td className="px-4 py-3 font-mono text-gray-900 whitespace-nowrap">{r.staff_no}</td>
                      <td className="px-4 py-3 text-gray-900 whitespace-nowrap">{r.name}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{r.department || '—'}</td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                        {fmtDate(r.date)}
                        {r.is_pub_hol && <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">PH</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                        {fmtTime(r.ot_start)} – {fmtTime(r.ot_end)}
                        {r.is_overnight && <span className="ml-1 text-xs text-gray-400">(overnight)</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{fmtHours(r.ot_mins)}</td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{fmtTime(r.clock_in)}</td>
                      <td className={`px-4 py-3 whitespace-nowrap font-medium ${r.verification !== 'valid' && r.clock_out ? 'text-red-600' : 'text-gray-700'}`}>
                        {fmtTime(r.clock_out)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <StatusBadge status={r.verification} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OTVerification;
